import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, CheckCircle2, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import HeroDashboard from "@/components/HeroDashboard";
import { Reveal, MaskedLine, Marquee, StatCounter } from "@/components/Reveal";
import { COMPANY, SERVICES, WHY_CHOOSE, PROCESS, STATS, TRUST_ITEMS, TESTIMONIALS } from "@/data/site";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.35]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#0B1F3A] pt-[72px]" data-testid="hero-section">
      <div className="absolute inset-0 grid-bg" aria-hidden="true" />
      <div className="absolute inset-0 hero-glow" aria-hidden="true" />

      <div className="container-x relative grid items-center gap-14 py-16 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:py-28">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="eyebrow eyebrow-light font-mono-alt"
          >
            <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
            Web Portals &amp; IT Services · Sheffield, UK
          </motion.p>

          <h1 className="mt-5 font-display text-4xl font-bold leading-[1.08] text-white sm:text-5xl lg:text-[3.6rem]">
            <MaskedLine delay={0.2}>Web Portals &amp;</MaskedLine>
            <MaskedLine delay={0.34}>
              <span className="bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">IT Solutions</span> That
            </MaskedLine>
            <MaskedLine delay={0.48}>Power Your Business</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            LKTH LTD designs, builds and supports secure, scalable digital platforms for ambitious
            UK businesses — from customer portals and booking systems to cloud hosting and
            day-to-day IT support.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link to="/contact" className="btn-primary" data-testid="hero-primary-cta">
              Get a Free Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/services" className="btn-ghost-light" data-testid="hero-secondary-cta">
              Our Services
            </Link>
          </motion.div>
        </div>

        <motion.div style={{ y, opacity }} className="relative">
          <HeroDashboard />
        </motion.div>
      </div>

      <Marquee
        items={TRUST_ITEMS}
        duration={28}
        className="relative border-t border-white/10 py-5"
        render={(item) => (
          <span key={item.label} className="mx-6 inline-flex items-center gap-2.5 text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">
            <item.icon className="h-4 w-4 text-blue-400" aria-hidden="true" />
            {item.label}
            <span className="ml-6 h-1 w-1 rounded-full bg-blue-500/60" aria-hidden="true" />
          </span>
        )}
      />
    </section>
  );
}

function ServicesOverview() {
  return (
    <section className="section-pad bg-[#F8FAFC]" data-testid="home-services">
      <div className="container-x">
        <Reveal>
          <p className="eyebrow font-mono-alt">What we do</p>
          <div className="mt-4 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <h2 className="max-w-xl font-display text-3xl font-bold text-[#0B1F3A] sm:text-4xl">
              Everything your business needs to run online
            </h2>
            <Link to="/services" className="group inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600" data-testid="home-services-view-all">
              View all 8 services
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.slice(0, 6).map((s, i) => (
            <Reveal key={s.slug} delay={0.06 * i}>
              <Link
                to={`/services#${s.slug}`}
                data-testid={`home-service-card-${s.slug}`}
                className="card-soft group block h-full p-7"
              >
                <span className="icon-tile"><s.icon className="h-5 w-5" aria-hidden="true" /></span>
                <h3 className="mt-5 font-display text-lg font-semibold text-[#0B1F3A]">{s.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{s.short}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600">
                  Learn more
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section className="section-pad bg-white" data-testid="home-why-choose">
      <div className="container-x grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
        <Reveal>
          <p className="eyebrow font-mono-alt">Why LKTH LTD</p>
          <h2 className="mt-4 font-display text-3xl font-bold text-[#0B1F3A] sm:text-4xl">
            A partner that treats your business like its own
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-600">
            We are a small, focused team — which means you deal directly with the people building
            your systems, from the first call to every update after launch.
          </p>
          <Link to="/about" className="btn-outline mt-8" data-testid="why-choose-about-link">
            About our company
          </Link>
        </Reveal>

        <div>
          {WHY_CHOOSE.map((item, i) => (
            <Reveal key={item.title} delay={0.05 * i}>
              <div className="group flex gap-6 border-b border-slate-200 py-6 transition-colors first:pt-0 last:border-0 hover:border-blue-200">
                <span className="font-mono-alt text-sm font-bold text-blue-600/60 transition-colors group-hover:text-blue-600">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-display text-lg font-semibold text-[#0B1F3A]">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{item.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="section-pad bg-[#F8FAFC]" data-testid="home-process">
      <div className="container-x">
        <Reveal className="text-center">
          <p className="eyebrow font-mono-alt">How we work</p>
          <h2 className="mx-auto mt-4 max-w-xl font-display text-3xl font-bold text-[#0B1F3A] sm:text-4xl">
            A clear process, from first call to long-term support
          </h2>
        </Reveal>

        <div className="relative mt-16">
          <div className="absolute left-6 top-0 h-full w-px bg-slate-200 md:left-0 md:top-8 md:h-px md:w-full" aria-hidden="true" />
          <div className="grid gap-10 md:grid-cols-5 md:gap-4">
            {PROCESS.map((step, i) => (
              <Reveal key={step.number} delay={0.08 * i} className="relative pl-16 md:pl-0">
                <span className="absolute left-0 top-0 flex h-12 w-12 items-center justify-center rounded-full border border-blue-600/20 bg-white font-mono-alt text-sm font-bold text-blue-600 shadow-sm md:relative md:mb-6">
                  {step.number}
                </span>
                <h3 className="font-display text-lg font-semibold text-[#0B1F3A]">{step.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section className="bg-[#0B1F3A] py-16 sm:py-20" data-testid="home-stats">
      <div className="container-x grid grid-cols-2 gap-10 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={0.07 * i}>
            <StatCounter value={s.value} suffix={s.suffix} label={s.label} testid={s.testid} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const paused = useRef(false);

  useEffect(() => {
    const id = setInterval(() => {
      if (!paused.current) {
        setDir(1);
        setIndex((v) => (v + 1) % TESTIMONIALS.length);
      }
    }, 6500);
    return () => clearInterval(id);
  }, []);

  const go = (delta) => {
    setDir(delta);
    setIndex((v) => (v + delta + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  const t = TESTIMONIALS[index];

  return (
    <section className="section-pad bg-white" data-testid="home-testimonials">
      <div className="container-x">
        <Reveal className="text-center">
          <p className="eyebrow font-mono-alt">Testimonials</p>
          <h2 className="mx-auto mt-4 max-w-xl font-display text-3xl font-bold text-[#0B1F3A] sm:text-4xl">
            What our clients say
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            className="relative mx-auto mt-12 max-w-3xl"
            onMouseEnter={() => { paused.current = true; }}
            onMouseLeave={() => { paused.current = false; }}
          >
            <div className="card-soft relative overflow-hidden p-8 sm:p-12">
              <span className="absolute -top-2 left-8 select-none font-display text-[110px] font-bold leading-none text-blue-600/10" aria-hidden="true">“</span>
              <div className="relative min-h-[190px] sm:min-h-[150px]">
                <AnimatePresence mode="wait" custom={dir}>
                  <motion.figure
                    key={index}
                    custom={dir}
                    initial={{ opacity: 0, x: 40 * dir }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 * dir }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <blockquote className="text-base leading-relaxed text-slate-700 sm:text-lg">
                      {t.quote}
                    </blockquote>
                    <figcaption className="mt-6 flex items-center gap-4">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0B1F3A] font-display text-sm font-bold text-white" aria-hidden="true">
                        {t.author.charAt(0)}
                      </span>
                      <div>
                        <p className="font-display text-sm font-semibold text-[#0B1F3A]">{t.author}</p>
                        <p className="text-xs text-slate-500">{t.role} · {t.org}</p>
                      </div>
                    </figcaption>
                  </motion.figure>
                </AnimatePresence>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div className="flex gap-2" role="tablist" aria-label="Testimonials">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    role="tab"
                    aria-selected={i === index}
                    aria-label={`Testimonial ${i + 1}`}
                    onClick={() => { setDir(i > index ? 1 : -1); setIndex(i); }}
                    data-testid={`testimonial-dot-${i}`}
                    className={`h-2 rounded-full transition-all ${i === index ? "w-8 bg-blue-600" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => go(-1)} aria-label="Previous testimonial" data-testid="testimonial-prev-btn" className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-blue-600 hover:text-blue-600">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button onClick={() => go(1)} aria-label="Next testimonial" data-testid="testimonial-next-btn" className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-blue-600 hover:text-blue-600">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <p className="mt-6 text-center text-xs italic text-slate-400">
              Representative examples — client references available on request.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-[#1D4ED8] py-16 sm:py-20" data-testid="home-cta">
      <div className="absolute inset-0 grid-bg opacity-40" aria-hidden="true" />
      <div className="container-x relative flex flex-col items-center justify-between gap-8 text-center lg:flex-row lg:text-left">
        <div>
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">
            Ready to build something better?
          </h2>
          <p className="mt-3 max-w-xl text-blue-100">
            Tell us about your project and we will reply within one working day with honest,
            practical advice — and a clear quote.
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
          <Link to="/contact" className="btn-primary !bg-white !text-blue-700 shadow-none hover:!bg-blue-50" data-testid="home-cta-quote-btn">
            Get a Free Consultation
          </Link>
          <a href={COMPANY.phoneHref} className="btn-ghost-light" data-testid="home-cta-call-btn">
            <Phone className="h-4 w-4" /> {COMPANY.phoneDisplay}
          </a>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    document.title = "LKTH LTD | Web Portals & IT Solutions — Sheffield, UK";
  }, []);

  return (
    <>
      <Seo
        title="LKTH LTD | Web Portals & IT Solutions — Sheffield, UK"
        description="UK-based IT services consultancy building secure, scalable web portals, websites, cloud solutions and managed IT support. Sheffield-based, serving the whole UK."
      />
      <Hero />
      <ServicesOverview />
      <WhyChoose />
      <Process />
      <Stats />
      <Testimonials />
      <FinalCta />
    </>
  );
}
